import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import { Dialog, Box, Typography, IconButton, Button, TextField, Chip, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import SendIcon from '@material-ui/icons/Send';
import PersonIcon from '@material-ui/icons/Person';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import AssignmentIcon from '@material-ui/icons/Assignment';
import { useSelector } from 'react-redux';
import { AuthContext } from '../../App';
import { getSocket } from '../../utils/websocket';

const useStyles = makeStyles(() => ({
  dialog: {
    '& .MuiDialog-paper': {
      width: 520, maxWidth: '94vw',
      height: 640, maxHeight: '85vh',
      borderRadius: 16, overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    },
  },
  header: {
    flex: '0 0 auto',
    padding: '14px 18px',
    background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
    borderBottom: '1px solid #c7d2fe',
    display: 'flex', alignItems: 'center', gap: 12,
  },
  headerIcon: {
    width: 38, height: 38, borderRadius: 10,
    background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 2px 6px rgba(99,102,241,0.3)',
  },
  thread: {
    flex: 1, minHeight: 0, overflowY: 'auto',
    padding: '14px 18px',
    background: '#f8fafc',
    display: 'flex', flexDirection: 'column', gap: 8,
  },
  bubble: {
    maxWidth: '78%',
    padding: '8px 12px',
    borderRadius: 14,
    fontSize: '0.78rem', lineHeight: 1.4,
    wordBreak: 'break-word',
    boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
  },
  bubbleAdmin: {
    alignSelf: 'flex-end',
    background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
    color: '#fff',
    borderBottomRightRadius: 4,
  },
  bubbleDriver: {
    alignSelf: 'flex-start',
    background: '#fff',
    color: '#0f172a',
    border: '1px solid #e2e8f0',
    borderBottomLeftRadius: 4,
  },
  meta: {
    fontSize: '0.56rem', color: '#94a3b8',
    marginTop: 2,
  },
  metaAdmin: { textAlign: 'right', color: '#dbeafe' },
  inputRow: {
    flex: '0 0 auto',
    padding: '10px 14px',
    background: '#fff',
    borderTop: '1px solid #e2e8f0',
    display: 'flex', alignItems: 'flex-end', gap: 8,
  },
  input: {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      borderRadius: 12, fontSize: '0.8rem',
      background: '#f8fafc',
    },
  },
  sendBtn: {
    minWidth: 0, padding: '10px 14px',
    borderRadius: 12, textTransform: 'none', fontWeight: 700,
    fontSize: '0.78rem',
    background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
    color: '#fff',
    '&:disabled': {
      background: '#e2e8f0', color: '#94a3b8',
    },
  },
  emptyState: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', gap: 6,
    color: '#94a3b8',
  },
}));

function fmtTime(d) {
  if (!d) return '';
  const dt = new Date(d);
  const today = new Date();
  const sameDay = dt.toDateString() === today.toDateString();
  return sameDay
    ? dt.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })
    : dt.toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/**
 * Two-way chat between the operator and a specific driver. Loads the existing
 * thread on open, listens for `message:new` over WebSocket, and POSTs new
 * messages back to /planner-api/messages/:driverId.
 */
function ChatDriverDialog({ open, onClose, driverId, driverName, driverPhone, activeOT, onOpenLive }) {
  const classes = useStyles();
  const { token } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const threadRef = useRef(null);

  // Pull live vehicle position from the vehicles slice. We match by the
  // active OT's vehicleId/Plate (most reliable) and fall back to scanning
  // all vehicles for one whose `driverId` matches — covers the case where
  // the driver isn't currently on a route but we still have a recent fix.
  const vehicles = useSelector(s => s.vehicles?.list || []);
  const vehicleMatch = useMemo(() => {
    if (!open || !driverId) return null;
    if (activeOT?.vehicleId) {
      const byId = vehicles.find(v => Number(v.id) === Number(activeOT.vehicleId));
      if (byId) return byId;
    }
    if (activeOT?.vehiclePlate) {
      const byPlate = vehicles.find(v => (v.plate || '').toUpperCase() === String(activeOT.vehiclePlate).toUpperCase());
      if (byPlate) return byPlate;
    }
    return vehicles.find(v => Number(v.driverId) === Number(driverId)) || null;
  }, [open, driverId, activeOT, vehicles]);
  const livePos = vehicleMatch?.position;
  const fixAgeMin = livePos?.fixTime
    ? Math.max(0, Math.floor((Date.now() - new Date(livePos.fixTime).getTime()) / 60000))
    : null;

  const scrollToBottom = useCallback(() => {
    const el = threadRef.current;
    if (!el) return;
    requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, []);

  // Load thread + mark driver-side messages as read when opening.
  useEffect(() => {
    if (!open || !driverId || !token) return;
    setLoading(true);
    fetch(`/planner-api/messages/${driverId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { messages: [] })
      .then(data => {
        setMessages(Array.isArray(data?.messages) ? data.messages : []);
        scrollToBottom();
      })
      .catch(() => setMessages([]))
      .finally(() => setLoading(false));
    fetch(`/planner-api/messages/${driverId}/read`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    }).catch(() => {});
  }, [open, driverId, token, scrollToBottom]);

  // Live updates via WS — append messages for this driver only.
  useEffect(() => {
    if (!open || !driverId) return;
    const sock = getSocket();
    if (!sock) return;
    const onNew = (msg) => {
      if (Number(msg.driverId) !== Number(driverId)) return;
      setMessages(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg]);
      scrollToBottom();
    };
    sock.on('message:new', onNew);
    return () => { sock.off('message:new', onNew); };
  }, [open, driverId, scrollToBottom]);

  async function handleSend() {
    const body = input.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const res = await fetch(`/planner-api/messages/${driverId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ body }),
      });
      if (!res.ok) throw new Error('falha');
      const created = await res.json();
      // Optimistic append (WS will dedupe).
      setMessages(prev => prev.some(m => m.id === created.id) ? prev : [...prev, created]);
      setInput('');
      scrollToBottom();
    } catch (e) {
      console.error('send message error:', e);
    } finally {
      setSending(false);
    }
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog}>
      <Box className={classes.header} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Box className={classes.headerIcon}>
            <ChatBubbleOutlineIcon style={{ color: '#fff', fontSize: 20 }} />
          </Box>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Typography style={{ fontSize: '0.95rem', fontWeight: 800, color: '#1e293b' }} noWrap>
              Chat · {driverName || `Motorista #${driverId}`}
            </Typography>
            <Typography style={{ fontSize: '0.66rem', color: '#64748b' }}>
              {driverPhone ? `📞 ${driverPhone} · ` : ''}Notificação push entregue ao motorista.
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon style={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Context strip: active OT + live position. Both fields are optional —
            if the driver is offline / not on a route the chip shows so but the
            chat still works. */}
        <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {activeOT ? (
            <Chip
              size="small"
              icon={<AssignmentIcon style={{ fontSize: 14 }} />}
              label={`${activeOT.orderNumber || `OT #${activeOT.id}`} · ${activeOT.deliveryName || activeOT.deliveryAddress || 'Em curso'}`}
              style={{ background: '#dbeafe', color: '#1e40af', fontWeight: 600, fontSize: '0.66rem', height: 22 }}
            />
          ) : (
            <Chip
              size="small"
              icon={<AssignmentIcon style={{ fontSize: 14 }} />}
              label="Sem OT em trânsito"
              style={{ background: '#f1f5f9', color: '#64748b', fontSize: '0.66rem', height: 22 }}
            />
          )}
          {activeOT?.vehiclePlate && (
            <Chip
              size="small"
              icon={<LocalShippingIcon style={{ fontSize: 14 }} />}
              label={activeOT.vehiclePlate}
              style={{ background: '#fef3c7', color: '#92400e', fontWeight: 700, fontSize: '0.66rem', height: 22 }}
            />
          )}
          {livePos?.latitude != null && livePos?.longitude != null ? (
            <Chip
              size="small"
              clickable
              onClick={() => {
                const u = `https://www.google.com/maps?q=${livePos.latitude},${livePos.longitude}`;
                window.open(u, '_blank', 'noopener');
              }}
              icon={<LocationOnIcon style={{ fontSize: 14 }} />}
              label={`${Number(livePos.latitude).toFixed(4)}, ${Number(livePos.longitude).toFixed(4)}${fixAgeMin != null ? ` · há ${fixAgeMin}m` : ''}`}
              style={{ background: '#dcfce7', color: '#166534', fontSize: '0.62rem', height: 22 }}
            />
          ) : (
            <Chip
              size="small"
              icon={<LocationOnIcon style={{ fontSize: 14 }} />}
              label="Sem GPS"
              style={{ background: '#f1f5f9', color: '#94a3b8', fontSize: '0.62rem', height: 22 }}
            />
          )}
          {activeOT && onOpenLive && (
            <Button
              size="small"
              onClick={() => onOpenLive(activeOT)}
              style={{
                marginLeft: 'auto', textTransform: 'none', fontSize: '0.66rem', fontWeight: 700,
                color: '#1d4ed8', padding: '0 8px', minHeight: 22,
              }}
            >
              Ver no Live →
            </Button>
          )}
        </Box>
      </Box>

      <Box ref={threadRef} className={classes.thread}>
        {loading && (
          <Box style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
            <CircularProgress size={22} style={{ color: '#6366f1' }} />
          </Box>
        )}
        {!loading && messages.length === 0 && (
          <Box className={classes.emptyState}>
            <PersonIcon style={{ fontSize: 36, color: '#cbd5e1' }} />
            <Typography style={{ fontSize: '0.78rem', fontWeight: 600 }}>
              Sem mensagens — escreve a primeira.
            </Typography>
          </Box>
        )}
        {messages.map(m => (
          <Box key={m.id} style={{ display: 'flex', flexDirection: 'column', maxWidth: '100%' }}>
            <Box className={`${classes.bubble} ${m.fromAdmin ? classes.bubbleAdmin : classes.bubbleDriver}`}>
              {m.body}
            </Box>
            <Typography className={`${classes.meta} ${m.fromAdmin ? classes.metaAdmin : ''}`}>
              {m.fromAdmin ? (m.senderUserName || 'Operador') : (driverName || 'Motorista')} · {fmtTime(m.createdAt)}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box className={classes.inputRow}>
        <TextField
          className={classes.input}
          placeholder="Escreve uma mensagem..."
          variant="outlined"
          size="small"
          multiline
          maxRows={4}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={sending}
        />
        <Button
          className={classes.sendBtn}
          onClick={handleSend}
          disabled={sending || !input.trim()}
          startIcon={<SendIcon style={{ fontSize: 16 }} />}
        >
          Enviar
        </Button>
      </Box>
    </Dialog>
  );
}

export default ChatDriverDialog;
